import React, { useState } from 'react';
import { Preferences as Storage } from '@capacitor/preferences';
import { Center, Flex, Heading, Image, Text, useToast } from '@chakra-ui/react';
import { CapacitorWifiConnect } from '@falconeta/capacitor-wifi-connect';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { IS_NATIVE } from 'clipsal-cortex-utils/src/constants/common-constants';

import { useReduxDispatch } from '../../../app/store';
import chargerImg from '../../../assets/images/evse_charger_qr_code.svg';
import { ArcButton } from '../../../common/components/ArcButton';
import { SlidingAnimationPageBase } from '../../../common/components/SlidingAnimationPageBase';
import { IS_RUNNING_CYPRESS_TESTS } from '../../../common/constants';
import { timeout } from '../../../utils/common/common-utils';
import { WIFI_RECONNECT_TIMEOUT_MS } from '../constants';
import { getTestingQRCodeContent, parseQRCode } from '../ev-charger-helper';
import { useDebugLogs } from '../use-debug-logs';
import { fetchEVSECerts } from '../wifi-configuration/wifi-connect-helper';
import { BoltLoader } from './BoltLoader';
import { setQRCodeValues } from './deviceSetupSlice';
import { useQRCodeScan } from './use-qr-code-scan';

/* istanbul ignore next -- @preserve */
export const DeviceScan = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { debugToast } = useDebugLogs();
  const { scanQRCode, showInvalidQRCodeToast } = useQRCodeScan();
  const dispatch = useReduxDispatch();
  const [isConnecting, setIsConnecting] = useState(false);
  const isTestingBarcodeScanError = (window as Window & typeof globalThis).isTestingBarcodeScanError;
  const isTestingWifiConnectError = (window as Window & typeof globalThis).isTestingWifiConnectError;

  async function handleStartQRCodeScan() {
    // Handle QR code scan success and fail when running Cypress tests.
    const codeContent = IS_RUNNING_CYPRESS_TESTS
      ? getTestingQRCodeContent(isTestingBarcodeScanError)
      : await scanQRCode();

    const codeValues = parseQRCode(codeContent);
    if (!codeValues || !codeValues.cpid || !codeValues.sn || !codeValues.wifi) {
      Sentry.captureMessage('Invalid EVSE device QR code', {
        level: 'error',
        tags: {
          category: 'feature',
          feature: 'ev-charger',
          component: 'DeviceScan',
          method: 'parseQRCode',
        },
        extra: { codeContent, codeValues: JSON.stringify(codeValues) },
      });
      showInvalidQRCodeToast();
      return;
    }

    setIsConnecting(true);
    dispatch(setQRCodeValues(codeValues));

    try {
      // Fetch certificates for EVSE before connecting to EVSE Wi-Fi and becoming offline.
      const certs = await fetchEVSECerts();
      await Storage.set({ key: 'evseCerts', value: JSON.stringify(certs) });
      debugToast('Certs fetched', `Content Length: ${JSON.stringify(certs).length}`);
    } catch (error) {
      debugToast('Error fetching device certs', JSON.stringify(error));
      Sentry.captureException(error, {
        tags: {
          category: 'feature',
          feature: 'ev-charger',
          component: 'DeviceScan',
          method: 'fetchEVSECerts',
        },
      });
      toast({
        title: t('EVSE.error fetching device certs'),
        description: t('Common.if this persists contact support'),
        status: 'error',
        isClosable: true,
      });

      setIsConnecting(false);
      return;
    }

    try {
      let currentNetwork = null;
      if (IS_NATIVE) {
        await CapacitorWifiConnect.secureConnect({ ...codeValues.wifi });

        // Add delay to ensure connection is established.
        await timeout(WIFI_RECONNECT_TIMEOUT_MS);

        currentNetwork = await CapacitorWifiConnect.getDeviceSSID();
        debugToast('Current network', JSON.stringify(currentNetwork));
      }

      if (!currentNetwork?.value && (!IS_RUNNING_CYPRESS_TESTS || isTestingWifiConnectError)) {
        // Handle Wi-Fi connect success and fail when running Cypress tests.
        throw new Error('Could not connect to your Charging Station Wi-Fi Access Point');
      }

      // Check if user cancelled or connected to wrong network.
      if (!IS_RUNNING_CYPRESS_TESTS && currentNetwork?.value !== codeValues.wifi.ssid) {
        debugToast('Wrong network', `Connected to ${currentNetwork?.value}, expected ${codeValues.wifi.ssid}`);
        setIsConnecting(false);
        return;
      }

      navigate('../device-wifi-connection');
    } catch (error) {
      debugToast('Error connecting to wifi', JSON.stringify(error));
      Sentry.captureException(error, {
        tags: {
          category: 'feature',
          feature: 'ev-charger',
          component: 'DeviceScan',
          method: 'connect',
        },
      });
      toast({
        title: t('Set Up Hardware.error connecting to wifi'),
        description: t('Set Up Hardware.try again ensure wifi permission'),
        status: 'error',
        isClosable: true,
      });
    }

    setIsConnecting(false);
  }

  return (
    <SlidingAnimationPageBase title={t('Common.device setup')} backURL="../device-power-up-instructions">
      <Center flexDirection="column" px={3} textAlign="center">
        <Image src={chargerImg} alt="Charging Station QR Code Scan" ml={'5.5rem'} my={4} />

        {isConnecting ? (
          <>
            <BoltLoader />
            <Heading mt={2}>{t('EVSE.connecting to wifi')}</Heading>
            <Text mt={1}>{t('EVSE.connecting to wifi text')}</Text>
          </>
        ) : (
          <>
            <Heading mt={2}>{t('EVSE.allow camera title')}</Heading>

            <Flex direction={'column'}>
              <Text mt={1}>{t('EVSE.connect charger text 1')}</Text>
              <Flex alignSelf={'center'}>
                <Text fontWeight={600} color="#3DCD58">
                  {t('EVSE.right') + '\u00A0'}
                </Text>
                <Text>{t('EVSE.connect charger text 2')}</Text>
              </Flex>
            </Flex>

            <ArcButton
              data-testid="ev-charger-scan-device-qr"
              onClick={handleStartQRCodeScan}
              isDisabled={!IS_NATIVE && !IS_RUNNING_CYPRESS_TESTS}
              mt={8}
              w={'80%'}
              arcColor="#3DCD57"
            >
              {IS_NATIVE || IS_RUNNING_CYPRESS_TESTS
                ? t('Common.open camera')
                : t('Set Up Hardware.scanning not available')}
            </ArcButton>
          </>
        )}
      </Center>
    </SlidingAnimationPageBase>
  );
};
